import { AlertCircle, Copy, Download, Edit, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { useEffect, useMemo } from 'react';

import { useApplications } from '../../hooks/useApplications';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import { useCsvExport } from '../../hooks/useCsvExport';
import { useFormDialog } from '../../hooks/useFormDialog';
import { formatDate } from '../../utils/dateUtils';
import ConfirmDialog from '../common/ConfirmDialog';
import LoadingSpinner from '../common/LoadingSpinner';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { DataTable } from '../ui/data-table';
import { Dialog, DialogContent } from '../ui/dialog';
import { Switch } from '../ui/switch';

import ApplicationFormShadcn from './ApplicationFormShadcn';
import { getApplicationDisplayName } from './applicationPresentation';

const isExpired = expiresAt => !!expiresAt && new Date(expiresAt).getTime() <= Date.now();

const ApplicationList = () => {
  const {
    applications,
    loading,
    error,
    user,
    operationInProgress,
    fetchApplications,
    handleDelete,
    handleToggleActive,
    handleCopyApiKey,
    handleRegenerateApiKey,
    handleRevealApiKey,
    prepareExportData,
  } = useApplications();

  const {
    openForm,
    editItem: editApplication,
    handleAdd,
    handleEdit,
    handleClose,
  } = useFormDialog();

  const { confirmState, openConfirm, closeConfirm, handleConfirm } = useConfirmDialog();

  const { exportToCsv } = useCsvExport();

  // Rows for the desktop table. `displayLabel` gives the Name column one field to
  // sort AND search on that matches what the cell renders. Self-service keys are
  // stored under a `self-service:<clientId>:<name>` name but shown by their
  // friendly displayName — without this they render one label yet sort under "s"
  // and are effectively unfindable in the admin list.
  const tableData = useMemo(
    () =>
      (Array.isArray(applications) ? applications : []).map(app => ({
        ...app,
        displayLabel: getApplicationDisplayName(app),
      })),
    [applications]
  );

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  // Define columns for the modern data table - memoized to prevent re-creation
  const columns = useMemo(
    () => [
      {
        accessorKey: 'displayLabel',
        header: 'Name',
        sortable: true,
        className: 'min-w-[120px] w-auto',
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <span className="font-medium">{getApplicationDisplayName(row)}</span>
            {row.selfServiceManaged && (
              <Badge variant="secondary" className="text-[10px]">
                Self-service
              </Badge>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'description',
        header: 'Description',
        className: 'hidden md:table-cell min-w-[200px] w-auto',
        cell: ({ row }) => <div className="text-muted-foreground">{row.description || ''}</div>,
      },
      {
        accessorKey: 'defaultRole.name',
        header: 'Role',
        sortable: true,
        className: 'hidden sm:table-cell min-w-[100px] w-auto',
        cell: ({ row }) => <div className="font-medium">{row.defaultRole?.name || 'None'}</div>,
      },
      {
        accessorKey: 'apiKey',
        header: 'API Key',
        className: 'hidden lg:table-cell min-w-[250px] w-auto',
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <code className="font-mono text-xs bg-muted px-2 py-1 rounded max-w-[150px] truncate">
              {row.apiKey}
            </code>
            {row.isNewKey && (
              <Badge variant="warning" className="text-xs">
                New Key
              </Badge>
            )}
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => {
                  // If user is admin, always use reveal (which copies automatically)
                  // If not admin, use the basic copy (only works for new keys)
                  if (user?.isAdmin) {
                    handleRevealApiKey(row._id);
                  } else {
                    handleCopyApiKey(row.apiKey);
                  }
                }}
                disabled={!user?.isAdmin && !row.isNewKey && row.apiKey.includes('•')}
                aria-label={user?.isAdmin ? 'Copy full API key (Admin)' : 'Copy API key'}
              >
                <Copy className="h-3 w-3" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => {
                  // Prevent multiple rapid clicks
                  if (operationInProgress[`regenerate-${row._id}`]) {
                    return;
                  }
                  handleRegenerateApiKey(row._id);
                }}
                aria-label="Regenerate API key"
              >
                <RefreshCw className="h-3 w-3" />
              </Button>
            </div>
          </div>
        ),
      },
      {
        accessorKey: 'expiresAt',
        header: 'Expires',
        sortable: true,
        className: 'hidden lg:table-cell min-w-[140px] w-auto',
        cell: ({ value }) =>
          value ? (
            <Badge variant={isExpired(value) ? 'destructive' : 'secondary'} className="text-xs">
              {isExpired(value) ? 'Expired' : formatDate.date(value)}
            </Badge>
          ) : (
            <span className="text-sm text-muted-foreground">Never</span>
          ),
      },
      {
        accessorKey: 'isActive',
        header: 'Status',
        type: 'switch',
        className: 'min-w-[100px] w-auto',
        cell: ({ row, value }) => (
          <div className="flex items-center gap-2">
            <Switch
              checked={value}
              onCheckedChange={() => {
                // Prevent multiple rapid clicks
                if (operationInProgress[`toggle-${row._id}`]) {
                  return;
                }
                handleToggleActive(row);
              }}
              aria-label={`Toggle status for ${row.name}`}
              className="data-[state=checked]:bg-ocean-500 data-[state=unchecked]:bg-gray-200"
            />
            <Badge
              variant={value ? 'active' : 'inactive'}
              className="text-xs hidden sm:inline-flex"
            >
              {value ? 'Active' : 'Inactive'}
            </Badge>
          </div>
        ),
      },
      {
        accessorKey: 'actions',
        header: 'Actions',
        type: 'actions',
        className: 'min-w-[80px] w-auto',
        actions: [
          {
            label: 'Edit Application',
            icon: Edit,
            onClick: handleEdit,
          },
          {
            label: 'Regenerate API Key',
            icon: RefreshCw,
            onClick: application => {
              // Prevent multiple rapid clicks
              if (operationInProgress[`regenerate-${application._id}`]) {
                return;
              }
              handleRegenerateApiKey(application._id);
            },
            separator: true,
          },
          {
            label: 'Delete Application',
            icon: Trash2,
            onClick: application => {
              // Prevent multiple rapid clicks
              if (operationInProgress[`delete-${application._id}`]) {
                return;
              }
              openConfirm(application._id, {
                title: 'Delete Application',
                message:
                  'Are you sure you want to delete this application? This action cannot be undone.',
              });
            },
            destructive: true,
          },
        ],
      },
    ],
    [
      handleToggleActive,
      handleCopyApiKey,
      handleRegenerateApiKey,
      handleRevealApiKey,
      handleEdit,
      openConfirm,
      user,
      operationInProgress,
    ]
  );

  if (loading) return <LoadingSpinner />;

  return (
    <div className="flex flex-col h-full p-4 sm:p-6 space-y-4 sm:space-y-6">
      {/* Error and Success Messages */}
      {error && (
        <Card className="border-destructive/50 bg-destructive/10">
          <CardContent className="flex items-center gap-2 p-4">
            <AlertCircle className="h-4 w-4 text-destructive" />
            <span className="text-destructive font-medium">{error}</span>
          </CardContent>
        </Card>
      )}

      {/* Header - Responsive */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="text-center sm:text-left">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-ocean-800">
            Applications
          </h1>
          <p className="text-muted-foreground text-sm sm:text-base">
            Manage your API applications and access keys
          </p>
        </div>
        <div className="flex items-center gap-3 w-full sm:w-auto justify-center sm:justify-end">
          <Button
            variant="ocean"
            size="sm"
            className="flex-1 sm:flex-none bg-ocean-500 text-white hover:bg-ocean-600 border-ocean-500"
            onClick={() => exportToCsv(prepareExportData(), 'applications-list.csv')}
          >
            <Download className="mr-2 h-4 w-4" />
            <span className="hidden sm:inline">Export</span>
          </Button>
          <Button
            size="sm"
            className="flex-1 sm:flex-none bg-ocean-500 text-white hover:bg-ocean-600 border-ocean-500"
            onClick={() => handleAdd()}
          >
            <Plus className="mr-2 h-4 w-4" />
            <span className="sm:hidden">Add</span>
            <span className="hidden sm:inline">Add Application</span>
          </Button>
        </div>
      </div>

      {/* Mobile Card List */}
      <div className="sm:hidden space-y-3">
        {applications.map(app => (
          <Card key={app._id}>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="font-semibold text-base">{getApplicationDisplayName(app)}</div>
                  <div className="text-xs text-muted-foreground">
                    Role: {app.defaultRole?.name || 'None'}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Expires:{' '}
                    {app.expiresAt
                      ? `${formatDate.date(app.expiresAt)}${isExpired(app.expiresAt) ? ' (expired)' : ''}`
                      : 'Never'}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={!!app.isActive}
                    onCheckedChange={() => handleToggleActive(app._id, !app.isActive)}
                    aria-label={app.isActive ? 'Disable application' : 'Enable application'}
                  />
                </div>
              </div>

              {app.description && (
                <p className="text-sm text-muted-foreground">{app.description}</p>
              )}

              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <code className="font-mono text-xs bg-muted px-2 py-1 rounded max-w-[160px] truncate inline-block">
                    {app.apiKey}
                  </code>
                  {app.isNewKey && (
                    <Badge variant="warning" className="text-[10px] ml-2 align-middle">
                      New Key
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      user?.isAdmin ? handleRevealApiKey(app._id) : handleCopyApiKey(app.apiKey)
                    }
                    disabled={!user?.isAdmin && !app.isNewKey && app.apiKey?.includes('�?�')}
                    aria-label={user?.isAdmin ? 'Copy full API key (Admin)' : 'Copy API key'}
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (operationInProgress[`regenerate-${app._id}`]) return;
                      handleRegenerateApiKey(app._id);
                    }}
                    aria-label="Regenerate API key"
                  >
                    <RefreshCw className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(app)}
                    aria-label="Edit"
                  >
                    <Edit className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() =>
                      openConfirm(app._id, {
                        title: 'Delete Application',
                        message:
                          'Are you sure you want to delete this application? This action cannot be undone.',
                      })
                    }
                    aria-label="Delete"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Table for >= sm screens */}
      <div className="hidden sm:block">
        <DataTable
          data={tableData}
          columns={columns}
          searchable={true}
          filterable={true}
          exportable={false} // We handle export in header
          loading={loading}
          defaultSort={{ key: 'displayLabel', direction: 'asc' }}
        />
      </div>

      <Dialog open={openForm} onOpenChange={open => !open && handleClose()}>
        <DialogContent className="sm:max-w-md">
          <ApplicationFormShadcn
            application={editApplication}
            onSubmitted={() => {
              handleClose();
              fetchApplications();
            }}
            onCancel={handleClose}
          />
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmState.open}
        title={confirmState.title}
        message={confirmState.message}
        onConfirm={() => handleConfirm(handleDelete)}
        onCancel={closeConfirm}
      />
    </div>
  );
};

export default ApplicationList;
